// ============================================================================
// DEVICES TAB - Device pairing, room binding, and device configuration
// ============================================================================

import { useState, useEffect, useCallback, useRef, Fragment } from 'react';
import {
  listSessionDevicesViaN8n,
  listAvailableDevicesViaN8n,
  assignDeviceViaN8n,
  unassignDeviceViaN8n,
  renewDeviceLeaseViaN8n,
  DeviceInfo,
  CAMERA_SLOTS,
  GLASSES_SLOTS,
  MICROPHONE_SLOTS,
  isCameraRole,
  isGlassesRole,
  formatSlotRole,
} from '../services/n8n';
import {
  configureDeviceCapture,
  toggleDeviceRecording,
  toggleVoiceAgent,
  queueDeviceEmotion,
  setDeviceSleepState,
  toggleFollowMe,
} from '../services/deviceImages';
import { setDeviceVolume } from '../services/deviceManagement';
import {
  Monitor,
  Camera,
  Battery,
  BatteryCharging,
  Wifi,
  Plus,
  RefreshCw,
  X,
  Clock,
  Loader,
  Glasses,
  Eye,
  EyeOff,
  Mic,
  MicOff,
  MessageCircle,
  Building2,
  MapPin,
  Link2,
  Unlink,
  ChevronDown,
  Zap,
  Moon,
  Sun,
  Volume2,
} from 'lucide-react';

const EMOTION_GROUPS = [
  {
    label: 'Positive',
    color: 'text-emerald-700',
    emotions: [
      { key: 'happy', label: 'Happy' },
      { key: 'excited', label: 'Excited' },
      { key: 'laughing', label: 'Laughing' },
      { key: 'wheezing', label: 'Wheezing' },
      { key: 'surprised', label: 'Surprised' },
      { key: 'amazed', label: 'Amazed' },
      { key: 'proud', label: 'Proud' },
      { key: 'grateful', label: 'Grateful' },
      { key: 'dance', label: 'Dance' },
    ],
  },
  {
    label: 'Neutral',
    color: 'text-slate-600',
    emotions: [
      { key: 'thinking', label: 'Thinking' },
      { key: 'listening', label: 'Listening' },
      { key: 'attentive', label: 'Attentive' },
      { key: 'assertive', label: 'Assertive' },
      { key: 'whispering', label: 'Whispering' },
    ],
  },
  {
    label: 'Negative',
    color: 'text-rose-700',
    emotions: [
      { key: 'sad', label: 'Sad' },
      { key: 'tired', label: 'Tired' },
      { key: 'exhausted', label: 'Exhausted' },
      { key: 'frustrated', label: 'Frustrated' },
      { key: 'nervous', label: 'Nervous' },
      { key: 'scared', label: 'Scared' },
      { key: 'confused', label: 'Confused' },
      { key: 'lonely', label: 'Lonely' },
      { key: 'disgusted', label: 'Disgusted' },
      { key: 'angry', label: 'Angry' },
      { key: 'furious', label: 'Furious' },
      { key: 'evil', label: 'Evil' },
      { key: 'timid', label: 'Timid' },
    ],
  },
];
import {
  listAllRoomsWithStatus,
  reserveRoom,
  releaseRoom,
  renewRoomLease,
  RoomWithAvailability,
} from '../services/rooms';

interface SessionAvatar {
  role: string;
  name: string;
  emoji?: string;
  imageUrl?: string;
}

interface DevicesTabProps {
  sessionId: string;
  currentPhase: string;
  sessionAvatars?: SessionAvatar[];
  roomId?: string;
  roomName?: string;
  roomLeaseExpiresAt?: string;
  converseProvider?: 'off' | 'elevenlabs';
}

const DEFAULT_LEASE_DURATION = 480; // 8 hours in minutes

const LEASE_DURATIONS = [
  { label: '1 hour', value: 60 },
  { label: '2 hours', value: 120 },
  { label: '4 hours', value: 240 },
  { label: '8 hours (default)', value: 480 },
  { label: '24 hours', value: 1440 },
];

type LeaseStatus = 'active' | 'warning' | 'expired' | null;

function getLeaseStatus(device: DeviceInfo): { status: LeaseStatus; remainingMinutes: number } {
  if (!device.leaseExpiresAt) return { status: null, remainingMinutes: 0 };
  const remaining = new Date(device.leaseExpiresAt).getTime() - Date.now();
  const remainingMin = Math.max(0, Math.round(remaining / 60000));
  const total = (device.leaseDurationMinutes || DEFAULT_LEASE_DURATION) * 60000;
  const warningThreshold = total * 0.15; // warn at 15% remaining (~72 min for 8h)
  if (remaining <= 0) return { status: 'expired', remainingMinutes: 0 };
  if (remaining <= warningThreshold) return { status: 'warning', remainingMinutes: remainingMin };
  return { status: 'active', remainingMinutes: remainingMin };
}

function formatLeaseRemaining(minutes: number): string {
  if (minutes <= 0) return 'Expired';
  if (minutes >= 60) {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  }
  return `${minutes}m`;
}

const CAPTURE_INTERVALS = [
  { label: 'Disabled', value: 0 },
  { label: '30 seconds', value: 30 },
  { label: '1 minute', value: 60 },
  { label: '2 minutes', value: 120 },
  { label: '5 minutes', value: 300 },
];

export function DevicesTab({ sessionId, sessionAvatars = [], roomId, roomName, roomLeaseExpiresAt, converseProvider = 'elevenlabs' }: DevicesTabProps) {
  // Device state
  const [sessionDevices, setSessionDevices] = useState<DeviceInfo[]>([]);
  const [availableDevices, setAvailableDevices] = useState<DeviceInfo[]>([]);

  // Room binding state
  const [currentRoomId, setCurrentRoomId] = useState<string | undefined>(roomId);
  const [currentRoomName, setCurrentRoomName] = useState<string | undefined>(roomName);
  const [currentRoomLeaseExpiresAt, setCurrentRoomLeaseExpiresAt] = useState<string | undefined>(roomLeaseExpiresAt);

  // Sync room binding props when parent reloads session data (e.g. on page refresh)
  useEffect(() => {
    setCurrentRoomId(roomId);
    setCurrentRoomName(roomName);
    setCurrentRoomLeaseExpiresAt(roomLeaseExpiresAt);
  }, [roomId, roomName, roomLeaseExpiresAt]);
  const [allRoomsList, setAllRoomsList] = useState<RoomWithAvailability[]>([]);
  const [showRoomBind, setShowRoomBind] = useState(false);
  const [showRoomDropdown, setShowRoomDropdown] = useState(false);
  const [bindingRoom, setBindingRoom] = useState(false);
  const [releasingRoom, setReleasingRoom] = useState(false);
  const [renewingRoomLease, setRenewingRoomLease] = useState(false);
  const [showPairDropdown, setShowPairDropdown] = useState(false);
  const [pairRole, setPairRole] = useState('');
  const [leaseDuration, setLeaseDuration] = useState(DEFAULT_LEASE_DURATION);

  // Loading/error
  const [loadingDevices, setLoadingDevices] = useState(true);
  const [initialFetchDone, setInitialFetchDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Room handlers
  const handleBindRoom = async (selectedRoomId: string) => {
    setBindingRoom(true);
    try {
      const reservation = await reserveRoom(selectedRoomId, { sessionId });
      const room = allRoomsList.find(r => r.roomId === selectedRoomId);
      setCurrentRoomId(selectedRoomId);
      setCurrentRoomName(room?.name || selectedRoomId);
      setCurrentRoomLeaseExpiresAt(reservation.leaseExpiresAt);
      setShowRoomBind(false);
    } catch (e: any) {
      setError(e.response?.data?.error || e.message || 'Failed to bind room');
    } finally {
      setBindingRoom(false);
    }
  };

  const handleReleaseRoom = async () => {
    if (!currentRoomId) return;
    if (!window.confirm('Release this room? All room devices will be unassigned from this session.')) return;
    setReleasingRoom(true);
    try {
      await releaseRoom(currentRoomId, sessionId);
      setCurrentRoomId(undefined);
      setCurrentRoomName(undefined);
      setCurrentRoomLeaseExpiresAt(undefined);
    } catch (e: any) {
      setError(e.response?.data?.error || e.message || 'Failed to release room');
    } finally {
      setReleasingRoom(false);
    }
  };

  const handleRenewRoomLease = async () => {
    if (!currentRoomId) return;
    setRenewingRoomLease(true);
    try {
      const reservation = await renewRoomLease(currentRoomId, { sessionId });
      setCurrentRoomLeaseExpiresAt(reservation.leaseExpiresAt);
    } catch (e: any) {
      setError(e.response?.data?.error || e.message || 'Failed to renew room lease');
    } finally {
      setRenewingRoomLease(false);
    }
  };

  // Load available rooms when showing bind UI
  useEffect(() => {
    if (showRoomBind) {
      listAllRoomsWithStatus().then(setAllRoomsList).catch(console.error);
    }
  }, [showRoomBind]);

  // Capture config state per device
  const [captureConfigs, setCaptureConfigs] = useState<Record<string, number>>({});

  // Audio recording state per device
  const [recordingDevices, setRecordingDevices] = useState<Set<string>>(new Set());
  const [togglingRecording, setTogglingRecording] = useState<string | null>(null);

  // Voice agent state per device
  const [conversingDevices, setConversingDevices] = useState<Set<string>>(new Set());
  const [togglingVoiceAgent, setTogglingVoiceAgent] = useState<string | null>(null);

  // Sleep state per device
  const [sleepingDevices, setSleepingDevices] = useState<Set<string>>(new Set());
  const [togglingDeviceSleep, setTogglingDeviceSleep] = useState<string | null>(null);

  // Follow-me state per device
  const [followingDevices, setFollowingDevices] = useState<Set<string>>(new Set());
  const [togglingFollowMe, setTogglingFollowMe] = useState<string | null>(null);

  // Emotion panel state per device
  const [expandedEmotionDevices, setExpandedEmotionDevices] = useState<Set<string>>(new Set());
  const [playingEmotion, setPlayingEmotion] = useState<Record<string, string>>({});

  // Speaker volume per device (UI state — set-only; not read back from device)
  const [deviceVolumes, setDeviceVolumes] = useState<Record<string, number>>({});
  const [pendingVolume, setPendingVolume] = useState<Record<string, boolean>>({});
  const volumeDebounceRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const handleVolumeChange = useCallback((deviceId: string, value: number) => {
    setDeviceVolumes(prev => ({ ...prev, [deviceId]: value }));
    const timers = volumeDebounceRef.current;
    if (timers[deviceId]) clearTimeout(timers[deviceId]);
    timers[deviceId] = setTimeout(async () => {
      setPendingVolume(prev => ({ ...prev, [deviceId]: true }));
      try {
        await setDeviceVolume(deviceId, value);
      } catch (err) {
        console.error('Failed to set device volume', err);
      } finally {
        setPendingVolume(prev => ({ ...prev, [deviceId]: false }));
      }
    }, 350);
  }, []);

  const fetchDevices = useCallback(async () => {
    setLoadingDevices(true);
    try {
      // Only fetch available devices if a room is bound
      const sessionRes = await listSessionDevicesViaN8n(sessionId);

      if (currentRoomId) {
        const availableRes = await listAvailableDevicesViaN8n();
        // Fetch room device IDs to filter available devices to this room only
        let roomDeviceIds: Set<string> | null = null;
        try {
          const { listRoomDevices: fetchRoomDevices } = await import('../services/rooms');
          const roomMappings = await fetchRoomDevices(currentRoomId);
          roomDeviceIds = new Set(roomMappings.map(m => m.deviceId));
        } catch (e) {
          console.warn('Failed to fetch room devices for filtering:', e);
        }
        const allAvailable = availableRes.devices || [];
        setAvailableDevices(
          roomDeviceIds ? allAvailable.filter(d => roomDeviceIds!.has(d.deviceId)) : allAvailable
        );
      } else {
        setAvailableDevices([]);
      }

      const devices = sessionRes.devices || [];

      // Seed recording/conversing/sleeping state from device state
      const recording = new Set<string>();
      const conversing = new Set<string>();
      const sleeping = new Set<string>();
      for (const d of devices) {
        if (d.state === 'recording') recording.add(d.deviceId);
        if (d.state === 'conversing') conversing.add(d.deviceId);
        if (d.state === 'sleeping') sleeping.add(d.deviceId);
      }
      setRecordingDevices(recording);
      setConversingDevices(conversing);
      setSleepingDevices(sleeping);

      // Auto-unassign devices with expired leases
      const expired = devices.filter(d => getLeaseStatus(d).status === 'expired');
      if (expired.length > 0) {
        for (const device of expired) {
          try { await unassignDeviceViaN8n(device.deviceId); } catch { /* silent */ }
        }
        const updated = await listSessionDevicesViaN8n(sessionId);
        setSessionDevices(updated.devices || []);
      } else {
        setSessionDevices(devices);
      }
    } catch (err: any) {
      console.error('Failed to fetch devices:', err);
    } finally {
      setLoadingDevices(false);
      setInitialFetchDone(true);
    }
  }, [sessionId, currentRoomId]);

  // Initial load
  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  // Auto-refresh devices every 15 seconds
  useEffect(() => {
    const interval = setInterval(fetchDevices, 15000);
    return () => clearInterval(interval);
  }, [fetchDevices]);

  const handlePairDevice = async (deviceId: string) => {
    try {
      setError(null);
      await assignDeviceViaN8n(deviceId, pairRole, sessionId, leaseDuration);
      setShowPairDropdown(false);
      setPairRole('');
      setLeaseDuration(DEFAULT_LEASE_DURATION);
      await fetchDevices();
    } catch (err: any) {
      setError(`Failed to pair device: ${err.message}`);
    }
  };

  const handleUnpairDevice = async (deviceId: string) => {
    try {
      setError(null);
      await unassignDeviceViaN8n(deviceId);
      await fetchDevices();
    } catch (err: any) {
      setError(`Failed to unpair device: ${err.message}`);
    }
  };

  const handleRenewLease = async (deviceId: string) => {
    try {
      setError(null);
      await renewDeviceLeaseViaN8n(deviceId, sessionId);
      await fetchDevices();
    } catch (err: any) {
      setError(`Failed to renew lease: ${err.message}`);
    }
  };

  const handleConfigureCapture = async (deviceId: string, interval: number) => {
    try {
      setError(null);
      setCaptureConfigs(prev => ({ ...prev, [deviceId]: interval }));
      await configureDeviceCapture(deviceId, sessionId, interval, interval > 0);
    } catch (err: any) {
      setError(`Failed to configure capture: ${err.message}`);
    }
  };

  const handleToggleRecording = async (deviceId: string) => {
    try {
      setError(null);
      setTogglingRecording(deviceId);
      const isRecording = recordingDevices.has(deviceId);
      const action = isRecording ? 'stop-recording' : 'start-recording';
      await toggleDeviceRecording(deviceId, sessionId, action);
      setRecordingDevices(prev => {
        const next = new Set(prev);
        if (isRecording) {
          next.delete(deviceId);
        } else {
          next.add(deviceId);
        }
        return next;
      });
    } catch (err: any) {
      setError(`Failed to toggle recording: ${err.message}`);
    } finally {
      setTogglingRecording(null);
    }
  };

  const handleToggleVoiceAgent = async (device: DeviceInfo) => {
    try {
      setError(null);
      setTogglingVoiceAgent(device.deviceId);
      const isConversing = conversingDevices.has(device.deviceId);
      const action = isConversing ? 'stop' : 'start';
      await toggleVoiceAgent(device.deviceId, sessionId, action, device.assignedRole || undefined);
      setConversingDevices(prev => {
        const next = new Set(prev);
        if (isConversing) {
          next.delete(device.deviceId);
        } else {
          next.add(device.deviceId);
        }
        return next;
      });
    } catch (err: any) {
      setError(`Failed to toggle voice agent: ${err.message}`);
    } finally {
      setTogglingVoiceAgent(null);
    }
  };

  const hasCamera = (device: DeviceInfo) => {
    const caps = typeof device.capabilities === 'string'
      ? JSON.parse(device.capabilities)
      : (device.capabilities || []);
    return caps.includes('camera');
  };

  const hasMicrophone = (device: DeviceInfo) => {
    const caps = typeof device.capabilities === 'string'
      ? JSON.parse(device.capabilities)
      : (device.capabilities || []);
    return caps.includes('audio-capture') || device.deviceType === 'microphone';
  };

  const hasVoiceConversation = (device: DeviceInfo) => {
    const caps = getCapabilities(device);
    return caps.includes('voice-conversation');
  };

  const hasPhysicalAvatar = (device: DeviceInfo) => {
    return getCapabilities(device).includes('physical-avatar');
  };

  const handleToggleSleep = async (device: DeviceInfo) => {
    const isSleeping = sleepingDevices.has(device.deviceId);
    try {
      setTogglingDeviceSleep(device.deviceId);
      await setDeviceSleepState(device.deviceId, sessionId, isSleeping ? 'wake' : 'sleep');
      setSleepingDevices(prev => {
        const next = new Set(prev);
        if (isSleeping) next.delete(device.deviceId);
        else next.add(device.deviceId);
        return next;
      });
    } catch (err: any) {
      setError(`Failed to set device state: ${err.message}`);
    } finally {
      setTogglingDeviceSleep(null);
    }
  };

  const handleToggleFollowMe = async (device: DeviceInfo) => {
    const isFollowing = followingDevices.has(device.deviceId);
    try {
      setTogglingFollowMe(device.deviceId);
      await toggleFollowMe(device.deviceId, sessionId, !isFollowing);
      setFollowingDevices(prev => {
        const next = new Set(prev);
        if (isFollowing) next.delete(device.deviceId);
        else next.add(device.deviceId);
        return next;
      });
    } catch (err: any) {
      setError(`Failed to toggle follow-me: ${err.message}`);
    } finally {
      setTogglingFollowMe(null);
    }
  };

  const handlePlayEmotion = async (device: DeviceInfo, emotion: string) => {
    try {
      setPlayingEmotion(prev => ({ ...prev, [device.deviceId]: emotion }));
      await queueDeviceEmotion(device.deviceId, sessionId, emotion);
    } catch (err: any) {
      setError(`Failed to queue emotion: ${err.message}`);
    } finally {
      setTimeout(() => setPlayingEmotion(prev => {
        const next = { ...prev };
        if (next[device.deviceId] === emotion) delete next[device.deviceId];
        return next;
      }), 1500);
    }
  };

  const isARGlasses = (device: DeviceInfo) => {
    const caps = getCapabilities(device);
    return caps.includes('display') && caps.includes('ring-input');
  };

  const getCapabilities = (device: DeviceInfo): string[] => {
    return typeof device.capabilities === 'string'
      ? JSON.parse(device.capabilities)
      : (device.capabilities || []);
  };

  return (
    <div className="flex-1 overflow-y-auto p-8 font-display">
      <div className="w-full max-w-[1400px] mx-auto flex flex-col gap-6">
      {/* Error banner */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-sm text-red-800 flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700 ml-2">
            <X size={14} />
          </button>
        </div>
      )}

      {/* ============ ROOM BINDING ============ */}
      <div className="mb-6">
        {currentRoomId ? (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Building2 size={20} className="text-blue-600" />
              <div>
                <span className="font-semibold text-blue-900">{currentRoomName}</span>
                <span className="text-sm text-blue-600 ml-2">
                  Only devices in this room are shown
                </span>
                {currentRoomLeaseExpiresAt && (
                  <span className="text-xs text-blue-500 ml-2">
                    &middot; Lease expires {new Date(currentRoomLeaseExpiresAt).toLocaleTimeString()}
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleRenewRoomLease}
                disabled={renewingRoomLease}
                className="px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-100 rounded-lg hover:bg-blue-200 transition-colors disabled:opacity-50 flex items-center gap-1"
              >
                {renewingRoomLease ? <Loader size={12} className="animate-spin" /> : <Clock size={12} />}
                Renew Lease
              </button>
              <button
                onClick={handleReleaseRoom}
                disabled={releasingRoom}
                className="px-3 py-1.5 text-xs font-medium text-red-700 bg-red-100 rounded-lg hover:bg-red-200 transition-colors disabled:opacity-50 flex items-center gap-1"
              >
                {releasingRoom ? <Loader size={12} className="animate-spin" /> : <Unlink size={12} />}
                Release Room
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <MapPin size={20} className="text-slate-400" />
              <span className="text-sm text-slate-600">No room bound &mdash; bind a room to pair physical devices</span>
            </div>
            {showRoomBind ? (
              <div className="flex items-center gap-2">
                <div className="relative">
                  <button
                    onClick={() => setShowRoomDropdown(v => !v)}
                    disabled={bindingRoom}
                    className="flex items-center gap-2 text-sm px-3 py-1.5 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary disabled:opacity-50 min-w-[220px] justify-between"
                  >
                    <span className="text-slate-400">Select a room...</span>
                    <ChevronDown size={14} className="text-slate-400 flex-shrink-0" />
                  </button>
                  {showRoomDropdown && (
                    <div className="absolute right-0 top-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg z-20 w-72 max-h-64 overflow-y-auto">
                      {allRoomsList.length === 0 ? (
                        <div className="px-4 py-3 text-sm text-slate-500">No rooms configured</div>
                      ) : (
                        allRoomsList.map(room => (
                          <button
                            key={room.roomId}
                            onClick={() => {
                              if (room.available) {
                                setShowRoomDropdown(false);
                                handleBindRoom(room.roomId);
                              }
                            }}
                            disabled={!room.available}
                            className={`w-full text-left px-4 py-2.5 flex items-center justify-between gap-3 transition-colors first:rounded-t-xl last:rounded-b-xl ${room.available ? 'hover:bg-slate-50 cursor-pointer' : 'cursor-not-allowed bg-slate-50/50'}`}
                          >
                            <div className="min-w-0">
                              <div className={`font-medium text-sm truncate ${room.available ? 'text-slate-900' : 'text-slate-400'}`}>{room.name}</div>
                              <div className="text-xs text-slate-400">{room.locationId}</div>
                            </div>
                            {!room.available && (
                              <span className="text-xs font-medium text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full whitespace-nowrap flex-shrink-0">In Use</span>
                            )}
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
                {bindingRoom && <Loader size={14} className="animate-spin text-primary" />}
                <button onClick={() => { setShowRoomBind(false); setShowRoomDropdown(false); }} className="text-slate-400 hover:text-slate-600">
                  <X size={14} />
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowRoomBind(true)}
                className="px-3 py-1.5 text-xs font-medium text-primary bg-primary/10 rounded-lg hover:bg-primary/20 transition-colors flex items-center gap-1"
              >
                <Link2 size={12} /> Bind Room
              </button>
            )}
          </div>
        )}
      </div>

      {/* ============ PAIRED DEVICES ============ */}
      <section className="mb-12">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <Monitor size={20} className="text-primary" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900">Paired Devices ({sessionDevices.length})</h2>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => fetchDevices()}
              className="flex items-center justify-center rounded-xl h-10 px-4 border border-slate-200 bg-white text-sm font-bold hover:bg-slate-50 transition-colors"
            >
              <RefreshCw size={14} className="mr-2" />
              Refresh
            </button>
            {currentRoomId && (
              <button
                onClick={() => { setShowPairDropdown(!showPairDropdown); if (!showPairDropdown) fetchDevices(); }}
                className="flex items-center justify-center rounded-xl h-10 px-4 bg-primary text-white text-sm font-bold hover:brightness-105 transition-all"
              >
                <Plus size={14} className="mr-2" />
                Pair Device
              </button>
            )}
          </div>
        </div>

        {/* Pair device dropdown */}
        {showPairDropdown && currentRoomId && (
          <div className="bg-white border border-primary/20 rounded-xl p-4 mb-4 shadow-sm">
            <div className="flex gap-3 mb-3 items-center">
              <label className="text-sm font-medium text-slate-500">Assign to Avatar:</label>
              <select
                value={pairRole}
                onChange={e => setPairRole(e.target.value)}
                className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary bg-white"
              >
                {(() => {
                  const takenRoles = new Set(sessionDevices.map(d => d.assignedRole).filter(Boolean));
                  const availableCameraSlots = CAMERA_SLOTS.filter(s => !takenRoles.has(s));
                  const availableGlassesSlots = GLASSES_SLOTS.filter(s => !takenRoles.has(s));
                  const availableMicrophoneSlots = MICROPHONE_SLOTS.filter(s => !takenRoles.has(s));
                  return sessionAvatars.length > 0 ? (
                    <>
                      <option value="">Select a role...</option>
                      {sessionAvatars.map(avatar => (
                        <option key={avatar.role} value={avatar.role}>
                          {avatar.emoji ? `${avatar.emoji} ` : ''}{avatar.name} ({avatar.role})
                        </option>
                      ))}
                      {availableCameraSlots.map(slot => (
                        <option key={slot} value={slot}>{formatSlotRole(slot)}</option>
                      ))}
                      {availableGlassesSlots.map(slot => (
                        <option key={slot} value={slot}>{formatSlotRole(slot)} (facilitator HUD)</option>
                      ))}
                      {availableMicrophoneSlots.map(slot => (
                        <option key={slot} value={slot}>{formatSlotRole(slot)} (room audio)</option>
                      ))}
                    </>
                  ) : (
                    <>
                      {availableCameraSlots.map(slot => (
                        <option key={slot} value={slot}>{formatSlotRole(slot)}</option>
                      ))}
                      <option value="whiteboard">Whiteboard</option>
                      {availableGlassesSlots.map(slot => (
                        <option key={slot} value={slot}>{formatSlotRole(slot)} (facilitator HUD)</option>
                      ))}
                      {availableMicrophoneSlots.map(slot => (
                        <option key={slot} value={slot}>{formatSlotRole(slot)} (room audio)</option>
                      ))}
                    </>
                  );
                })()}
              </select>
            </div>
            <div className="flex gap-3 mb-3 items-center">
              <label className="text-sm font-medium text-slate-500">Lease Duration:</label>
              <select
                value={leaseDuration}
                onChange={e => setLeaseDuration(parseInt(e.target.value))}
                className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary bg-white"
              >
                {LEASE_DURATIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            {availableDevices.length === 0 ? (
              <div className="text-sm text-slate-400 py-2">No available devices found</div>
            ) : (
              <div className="space-y-2">
                {availableDevices.map(device => (
                  <div key={device.deviceId} className="flex justify-between items-center p-3 rounded-lg bg-slate-50 border border-slate-100">
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-semibold text-slate-900">{device.displayName || device.deviceId}</span>
                      <span className="text-xs text-slate-400">{device.displayName && device.displayName !== device.deviceId ? device.deviceId + ' · ' : ''}{device.locationId}</span>
                      <span className="text-xs text-slate-500">
                        {getCapabilities(device).join(', ')}
                      </span>
                    </div>
                    <button
                      onClick={() => handlePairDevice(device.deviceId)}
                      disabled={!pairRole}
                      className="px-3 py-1.5 bg-primary text-white rounded-lg text-xs font-bold hover:brightness-105 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Pair
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Session device cards */}
        {sessionDevices.length === 0 ? (
          <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden text-center py-10 text-slate-400">
            <Monitor size={32} className="mx-auto mb-3 opacity-40" />
            <p className="text-sm">No devices paired to this session</p>
          </div>
        ) : (
          <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#e2e8f0] bg-slate-50/50">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Device</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Role</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Status</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Lease</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Controls</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#e2e8f0]">
                  {[...sessionDevices].sort((a, b) => a.deviceId.localeCompare(b.deviceId)).map(device => (
                    <Fragment key={device.deviceId}>
                      <tr>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Monitor size={16} className="text-slate-400 flex-shrink-0" />
                            <div>
                              <div className="font-semibold text-slate-900">{device.displayName || device.deviceId}</div>
                              <div className="text-xs text-slate-400">{device.deviceId}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span title={device.assignedRole || undefined} className={`text-xs font-bold px-2 py-0.5 rounded-full uppercase max-w-[8rem] truncate inline-flex items-center ${
                            isCameraRole(device.assignedRole)
                              ? 'bg-primary/10 text-primary'
                              : isGlassesRole(device.assignedRole)
                              ? 'bg-purple-100 text-purple-700'
                              : 'bg-slate-100 text-slate-600'
                          }`}>
                            {isGlassesRole(device.assignedRole) ? (
                              <span className="flex items-center gap-1"><Glasses size={10} /> {formatSlotRole(device.assignedRole!)}</span>
                            ) : isCameraRole(device.assignedRole) ? (
                              <span className="flex items-center gap-1"><Camera size={10} /> {formatSlotRole(device.assignedRole!)}</span>
                            ) : (device.assignedRole || 'n/a')}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3 text-xs text-slate-500">
                            <span className="flex items-center gap-1">
                              {device.isCharging
                                ? <BatteryCharging size={12} className="text-emerald-500" />
                                : <Battery size={12} />}
                              {device.batteryLevel}%
                            </span>
                            <span className={`flex items-center gap-1 font-medium ${
                              device.state === 'online' ? 'text-emerald-500' : 'text-slate-400'
                            }`}>
                              <Wifi size={12} />
                              {hasPhysicalAvatar(device) ? null : device.state}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {(() => {
                            const lease = getLeaseStatus(device);
                            if (!lease.status) return <span className="text-xs text-slate-400">—</span>;
                            return (
                              <div className={`flex items-center gap-2 text-xs font-medium ${
                                lease.status === 'active' ? 'text-emerald-700' :
                                lease.status === 'warning' ? 'text-amber-700' :
                                'text-red-700'
                              }`}>
                                <Clock size={12} />
                                <span>{lease.status === 'expired' ? 'Expired' : formatLeaseRemaining(lease.remainingMinutes)}</span>
                                {(lease.status === 'warning' || lease.status === 'expired') && (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleRenewLease(device.deviceId); }}
                                    className="px-2 py-0.5 bg-primary text-white rounded text-xs font-bold hover:brightness-105"
                                  >
                                    Renew
                                  </button>
                                )}
                              </div>
                            );
                          })()}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {hasPhysicalAvatar(device) && (
                              <>
                                <button
                                  onClick={() => handleToggleSleep(device)}
                                  disabled={togglingDeviceSleep === device.deviceId}
                                  title={sleepingDevices.has(device.deviceId) ? 'Wake Up' : 'Sleep'}
                                  className={`flex items-center justify-center w-8 h-8 rounded-lg text-white transition-all disabled:opacity-50 ${
                                    sleepingDevices.has(device.deviceId)
                                      ? 'bg-primary hover:brightness-105'
                                      : 'bg-slate-500 hover:bg-slate-600'
                                  }`}
                                >
                                  {togglingDeviceSleep === device.deviceId
                                    ? <Loader size={14} className="animate-spin" />
                                    : sleepingDevices.has(device.deviceId) ? <Sun size={14} /> : <Moon size={14} />}
                                </button>
                                <button
                                  onClick={() => handleToggleFollowMe(device)}
                                  disabled={togglingFollowMe === device.deviceId || sleepingDevices.has(device.deviceId)}
                                  title={followingDevices.has(device.deviceId) ? 'Stop Follow Me' : 'Follow Me'}
                                  className={`flex items-center justify-center w-8 h-8 rounded-lg text-white transition-all disabled:opacity-50 ${
                                    followingDevices.has(device.deviceId)
                                      ? 'bg-violet-500 hover:bg-violet-600'
                                      : 'bg-slate-500 hover:bg-slate-600'
                                  }`}
                                >
                                  {togglingFollowMe === device.deviceId
                                    ? <Loader size={14} className="animate-spin" />
                                    : followingDevices.has(device.deviceId) ? <EyeOff size={14} /> : <Eye size={14} />}
                                </button>
                              </>
                            )}
                            {hasVoiceConversation(device) && (
                              <button
                                onClick={() => handleToggleVoiceAgent(device)}
                                disabled={togglingVoiceAgent === device.deviceId || !initialFetchDone || converseProvider === 'off'}
                                title={conversingDevices.has(device.deviceId) ? 'End Conversation' : 'Start Voice Agent'}
                                className={`flex items-center justify-center w-8 h-8 rounded-lg text-white transition-all disabled:opacity-50 ${
                                  conversingDevices.has(device.deviceId)
                                    ? 'bg-red-500 hover:bg-red-600'
                                    : 'bg-primary hover:brightness-105'
                                }`}
                              >
                                {(togglingVoiceAgent === device.deviceId || (!initialFetchDone && loadingDevices))
                                  ? <Loader size={14} className="animate-spin" />
                                  : <MessageCircle size={14} />}
                              </button>
                            )}
                            {hasMicrophone(device) && (
                              <button
                                onClick={() => handleToggleRecording(device.deviceId)}
                                disabled={togglingRecording === device.deviceId}
                                title={recordingDevices.has(device.deviceId) ? 'Stop Recording' : 'Start Recording'}
                                className={`flex items-center justify-center w-8 h-8 rounded-lg text-white transition-all disabled:opacity-50 ${
                                  recordingDevices.has(device.deviceId)
                                    ? 'bg-red-500 hover:bg-red-600'
                                    : 'bg-primary hover:brightness-105'
                                }`}
                              >
                                {togglingRecording === device.deviceId
                                  ? <Loader size={14} className="animate-spin" />
                                  : recordingDevices.has(device.deviceId) ? <MicOff size={14} /> : <Mic size={14} />}
                              </button>
                            )}
                            {hasCamera(device) && !isARGlasses(device) && (
                              <select
                                value={captureConfigs[device.deviceId] ?? 0}
                                onChange={e => handleConfigureCapture(device.deviceId, parseInt(e.target.value))}
                                className="bg-white border border-slate-200 rounded text-xs px-2 py-1 focus:ring-primary focus:border-primary h-8"
                              >
                                {CAPTURE_INTERVALS.map(opt => (
                                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                              </select>
                            )}
                            {hasPhysicalAvatar(device) && (
                              <>
                                <div className="flex items-center gap-1.5">
                                  <Volume2 size={14} className="text-slate-400 flex-shrink-0" />
                                  <input
                                    type="range"
                                    min={0}
                                    max={100}
                                    step={5}
                                    value={deviceVolumes[device.deviceId] ?? 75}
                                    onChange={(e) => handleVolumeChange(device.deviceId, parseInt(e.target.value, 10))}
                                    className="w-20 h-1.5 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-primary"
                                  />
                                  {pendingVolume[device.deviceId] && (
                                    <Loader size={10} className="animate-spin text-primary" />
                                  )}
                                </div>
                                {device.assignedRole && (
                                  <button
                                    onClick={() => setExpandedEmotionDevices(prev => {
                                      const next = new Set(prev);
                                      if (next.has(device.deviceId)) next.delete(device.deviceId);
                                      else next.add(device.deviceId);
                                      return next;
                                    })}
                                    title="Emotions"
                                    className={`flex items-center justify-center w-8 h-8 rounded-lg transition-all ${
                                      expandedEmotionDevices.has(device.deviceId)
                                        ? 'bg-violet-500 text-white'
                                        : 'bg-violet-100 text-violet-600 hover:bg-violet-200'
                                    }`}
                                  >
                                    <Zap size={14} />
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => handleUnpairDevice(device.deviceId)}
                            className="text-red-500 text-xs font-bold px-2 py-0.5 rounded border border-red-200 hover:bg-red-50 transition-colors"
                          >
                            Unpair
                          </button>
                        </td>
                      </tr>
                      {expandedEmotionDevices.has(device.deviceId) && (
                        <tr>
                          <td colSpan={6} className="px-4 py-3 bg-violet-50">
                            <div className="mt-2 space-y-2">
                              {EMOTION_GROUPS.map(group => (
                                <div key={group.label}>
                                  <span className={`text-[10px] font-semibold uppercase tracking-wide ${group.color} block mb-1`}>{group.label}</span>
                                  <div className="flex flex-wrap gap-1">
                                    {group.emotions.map(({ key, label }) => {
                                      const isPlaying = playingEmotion[device.deviceId] === key;
                                      return (
                                        <button
                                          key={key}
                                          onClick={() => handlePlayEmotion(device, key)}
                                          disabled={!!playingEmotion[device.deviceId]}
                                          className={`px-2 py-0.5 rounded text-[11px] font-medium transition-all disabled:opacity-50 ${
                                            isPlaying
                                              ? 'bg-violet-600 text-white'
                                              : 'bg-white border border-violet-200 text-violet-700 hover:bg-violet-100'
                                          }`}
                                        >
                                          {isPlaying ? '▶ ' : ''}{label}
                                        </button>
                                      );
                                    })}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>
      </div>
    </div>
  );
}

export default DevicesTab;
